<template>
  <div>
    <div v-if="$vuetify.breakpoint.mdAndUp" class="event-card">
      <v-card
        class="event-card--discipline"
        :color="event.discipline.name"
        elevation="0"
      >
        <div class="d-flex flex-row">
          <div style="text-transform: capitalize">
            {{ $t("common:discipline." + event.discipline.name)
            }}<AppShowEventReferenceCode :event="event" force-current-color
              ><template #prepend> | </template></AppShowEventReferenceCode
            >
          </div>
          <div>
            <AppShowEventStatus :event="event" no-button block always-show />
          </div>
        </div>
      </v-card>
      <v-card
        :data-testid="event.id"
        :to="linkToEvent"
        :target="embeddedMode ? '_blank' : undefined"
        class="event-card"
        data-test="eventCard"
      >
        <EventCardAlert v-if="showAlert" :text="event.alertText" />
        <div :class="[backgroundCard, 'event-card--content']">
          <v-row no-gutters>
            <v-col cols="8">
              <div class="event-card--content--date-location">
                {{ eventDate }}
                <span class="line" />
                <AppShowCountry :country="event.country">
                  <template #append
                    >,
                    <span
                      :class="
                        event.venueAddress && mapModal ? 'place-modal-link' : ''
                      "
                      @click.prevent="
                        event.venueAddress && mapModal
                          ? $emit('placeDetails', event)
                          : null
                      "
                    >
                      {{ regionName }}, {{ event.place }}
                    </span>
                  </template>
                </AppShowCountry>
              </div>

              <div class="event-card--content--title">
                <b>
                  <AppShowEventName :event="event" disable-link />
                </b>
                <v-icon
                  v-if="event.isHighlighted"
                  class="star-icon"
                  style="font-size: 24px"
                  color="star"
                  >$iconStar</v-icon
                >
              </div>
              <div class="event-card--content--organiser">
                {{ event.organiser.name }}
                <template v-if="event.hostOrganisation">
                  | {{ event.hostOrganisation.name }}
                </template>
              </div>
              <div class="event-card--content--description">
                {{ stripHTML(event.description) }}
              </div>
              <div class="event-card--content--misc secondary--text">
                <template v-if="!event.noncompetition">
                  <div>
                    <AppShowCompetitionLevelNames
                      :value="event.competitionLevels"
                    />
                    <span v-show="event.competitions.length > 0" class="oval" />

                    <AppShowDifficultyLevels
                      :value="
                        event.competitions
                          ? event.competitions.map(c => c.difficultyLevel)
                          : null
                      "
                      range
                    />

                    <span v-show="eventcodeNames.length > 0" class="oval" />
                    <AppShowEventcodes :value="event.competitions" />
                  </div>
                  <AppShowEventCupComponent :event="event" />
                </template>
                <div v-else>
                  <AppShowEventCategories
                    :event-categories="event.eventCategories"
                    class="mr-2"
                  />
                  <AppShowProfQualificationsList
                    :prof-qualifications="event.profQualifications"
                    cut-off-list
                  />
                </div>
              </div>
            </v-col>

            <v-col cols="4">
              <AppShowRegistrationDeadline
                :event="event"
                show-applicable-check-schedule
                align-text="right"
              />
              <div class="event-card--content--logos">
                <div class="sponsor-logo"></div>
                <div class="organiser-logo">
                  <OrganizationLogo :organiser="event.organiser" />
                </div>
              </div>

              <div class="event-card--content--button">
                <AppShowRegistrationBtn
                  v-if="eventLink == 'registration'"
                  :event="event"
                />
                <AppShowResultsBtn
                  v-else-if="eventLink == 'results'"
                  margin-class="mr-2"
                />
              </div>

              <div class="event-card--content--selections">
                <v-tooltip v-if="!isNoncompetition" bottom>
                  <template #activator="{ on, attrs }">
                    <v-btn
                      :to="{
                        name: profilePage,
                        params: { eventId: event.id }
                      }"
                      data-test="eventScheduleButton"
                      class="event-space-icon"
                      icon
                      :aria-label="
                        $t('events:EventCard.Schedule.tooltip', 'Schedule')
                      "
                      v-bind="attrs"
                      :target="embeddedMode ? '_blank' : undefined"
                      v-on="on"
                    >
                      <v-icon :small="$vuetify.breakpoint.smAndDown"
                        >$info</v-icon
                      >
                    </v-btn>
                  </template>
                  <span>
                    {{
                      $t(
                        "events:EventCard.Schedule.tooltip",
                        "Schedule",
                        "Tooltip for schedule link in event card"
                      )
                    }}
                  </span>
                </v-tooltip>
                <v-tooltip bottom>
                  <template #activator="{ on, attrs }">
                    <v-btn
                      :disabled="!event.startingListUrl"
                      :href="startingListUrl"
                      icon
                      class="event-space-icon"
                      target="_blank"
                      :aria-label="
                        $t(
                          'events:EventCard.StartingList.tooltip',
                          'Starting list'
                        )
                      "
                      v-bind="attrs"
                      v-on="on"
                      @click="forceLink($event, startingListUrl)"
                    >
                      <v-icon :small="$vuetify.breakpoint.smAndDown"
                        >$iconClipboard</v-icon
                      >
                    </v-btn>
                  </template>
                  <span>
                    {{
                      $t(
                        "events:EventCard.StartingList.tooltip",
                        "Starting list",
                        "Tooltip for starting list link in event card"
                      )
                    }}
                  </span>
                </v-tooltip>
                <v-tooltip bottom>
                  <template #activator="{ on, attrs }">
                    <v-btn
                      :disabled="!venueExists"
                      icon
                      class="event-space-icon"
                      v-bind="attrs"
                      :aria-label="$t('events:EventCard.venue.label', 'Venue')"
                      v-on="on"
                      @click.native.prevent="
                        venueExists && mapModal
                          ? $emit('placeDetails', event)
                          : null
                      "
                    >
                      <v-icon
                        :class="{
                          'muted--text': !venueExists
                        }"
                        :size="$vuetify.breakpoint.smAndDown ? 18 : 24"
                      >
                        $iconMap
                      </v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t("events:EventCard.venue.label", "Venue") }}</span>
                </v-tooltip>
                <v-tooltip v-if="!isNoncompetition" bottom>
                  <template #activator="{ on, attrs }">
                    <v-btn
                      :to="{
                        name: 'EventResultsPage',
                        params: { eventId: event.id }
                      }"
                      data-test="eventResultsButton"
                      class="event-space-icon"
                      icon
                      :aria-label="
                        $t('events:EventCard.Results.tooltip', 'Results')
                      "
                      :target="embeddedMode ? '_blank' : undefined"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon :small="$vuetify.breakpoint.smAndDown"
                        >$iconTrophy</v-icon
                      >
                    </v-btn>
                  </template>
                  <span>
                    {{
                      $t(
                        "events:EventCard.Results.tooltip",
                        "Results",
                        "Tooltip for results link in event card"
                      )
                    }}
                  </span>
                </v-tooltip>
                <v-tooltip bottom>
                  <template #activator="{ on, attrs }">
                    <v-btn
                      :href="broadcastUrl"
                      :disabled="!event.broadcastUrl"
                      class="event-space-icon"
                      icon
                      :aria-label="$t('events:EventCard.Live.tooltip')"
                      v-bind="attrs"
                      v-on="on"
                      @click="forceLink($event, broadcastUrl)"
                    >
                      <v-icon :small="$vuetify.breakpoint.smAndDown"
                        >$iconRecorder</v-icon
                      >
                    </v-btn>
                  </template>
                  <span>
                    {{
                      $t(
                        "events:EventCard.Live.tooltip",
                        "Live",
                        "Tooltip for LIVE link in event card"
                      )
                    }}
                  </span>
                </v-tooltip>
                <v-tooltip bottom>
                  <template #activator="{ on, attrs }">
                    <v-btn
                      :disabled="!event.homepageUrl"
                      :href="homepageUrl"
                      class="event-space-icon"
                      icon
                      :aria-label="
                        $t('events:EventCard.Webpage.tooltip', 'Webpage')
                      "
                      target="_blank"
                      v-bind="attrs"
                      v-on="on"
                      @click="forceLink($event, homepageUrl)"
                    >
                      <v-icon :small="$vuetify.breakpoint.smAndDown"
                        >$iconGlobe</v-icon
                      >
                    </v-btn>
                  </template>
                  <span>
                    {{
                      $t(
                        "events:EventCard.Webpage.tooltip",
                        "Webpage",
                        "Tooltip for webpage link in event card"
                      )
                    }}
                  </span>
                </v-tooltip>
              </div>
            </v-col>
          </v-row>
        </div>
      </v-card>
    </div>
    <div v-else>
      <EventCardVertical
        :event="event"
        class="ma-auto"
        :map-modal="mapModal"
        @placeDetails="$emit('placeDetails', $event)"
      />
    </div>
  </div>
</template>

<script>
import { inject, computed } from "vue";
import gql from "fraql";
import { truncate, uniq } from "lodash-es";
import { useEventUrlHelpers } from "@/components/event/mixins/eventUrlMixin";
import { useEventCardHelpers } from "./EventCardMixin";

import EventCardVertical from "./EventCardVertical.vue";
import AppShowEventCupComponent from "@/components/elements/AppShows/event/AppShowEventCupComponent.vue";
import AppShowCountry from "@AppShows/AppShowCountry.vue";
import EventCardAlert from "./EventCardAlert.vue";
import OrganizationLogo from "@/components/elements/AppOrganLogo.vue";
import AppShowEventStatus from "@/components/elements/AppShows/event/AppShowEventStatus.vue";
import AppShowDifficultyLevels from "@/components/elements/AppShows/AppShowDifficultyLevels.vue";
import AppShowCompetitionLevelNames from "@/components/elements/AppShows/event/AppShowCompetitionLevelNames.vue";
import AppShowRegistrationBtn from "@/components/elements/AppShows/event/AppShowRegistrationBtn.vue";
import AppShowEventcodes from "@/components/elements/AppShows/event/AppShowEventcodes.vue";
import AppShowEventName from "@AppShows/event/AppShowEventName.vue";
import AppShowRegistrationDeadline from "@AppShows/event/AppShowRegistrationDeadline.vue";
import AppShowResultsBtn from "@AppShows/event/AppShowResultsBtn.vue";
import AppShowEventCategories from "@AppShows/event/AppShowEventCategories.vue";
import AppShowProfQualificationsList from "@AppShows/AppShowProfQualificationsList.vue";
import eventVenueExists from "./venueExists.ts";
import AppShowEventReferenceCode from "@AppShows/event/AppShowEventReferenceCode.vue";

export default {
  name: "EventCard",
  components: {
    EventCardAlert,
    EventCardVertical,
    OrganizationLogo,
    AppShowEventCupComponent,
    AppShowDifficultyLevels,
    AppShowCompetitionLevelNames,
    AppShowEventStatus,
    AppShowRegistrationBtn,
    AppShowEventcodes,
    AppShowCountry,
    AppShowEventName,
    AppShowRegistrationDeadline,
    AppShowResultsBtn,
    AppShowEventCategories,
    AppShowProfQualificationsList,
    AppShowEventReferenceCode
  },
  inject: {
    isNoncompetition: {
      default: false
    }
  },
  props: {
    event: {
      type: Object,
      required: true
    },
    mapModal: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const embeddedMode = inject("embeddedMode", false);

    const { homepageUrl, broadcastUrl, startingListUrl } = useEventUrlHelpers(
      props.event
    );

    const {
      profilePage,
      regionName,
      eventDate,
      eventLink,
      showAlert,

      linkToEvent
    } = useEventCardHelpers(props);

    const eventcodeNames = computed(() => {
      return uniq(
        (props.event.competitions ?? []).reduce(function (arr, item) {
          if (item.eventcode) arr.push(item.eventcode.name);
          return arr;
        }, [])
      );
    });

    const venueExists = computed(() => {
      return eventVenueExists(props.event);
    });

    const backgroundCard = computed(() => {
      if (["proposed", "draft"].includes(props.event.status)) {
        return "entsys--private-event";
      }
      return "";
    });

    function stripHTML(description) {
      if (!description) return "";
      // Using this trick to strip away HTML tags
      return truncate(
        new DOMParser().parseFromString(description, "text/html").body
          .innerText,
        {
          length: 280
        }
      );
    }

    function forceLink(evt, link) {
      evt.preventDefault();
      window.open(link, "_blank");
    }

    return {
      embeddedMode,
      profilePage,
      regionName,
      eventDate,
      eventLink,
      showAlert,
      linkToEvent,

      homepageUrl,
      broadcastUrl,
      startingListUrl,

      eventcodeNames,
      venueExists,
      backgroundCard,
      stripHTML,
      forceLink
    };
  },
  fragments: {
    events: gql`
      fragment _ on Event {
        id
        name
        ${AppShowEventReferenceCode.fragments.eventFields}
        ${AppShowEventStatus.fragments.eventStatus}
        ${AppShowRegistrationBtn.fragments.event}
        ${AppShowRegistrationDeadline.fragments.eventFields}
        startDate
        endDate
        isHighlighted
        registrationEndDate
        description
        alertText
        startingListUrl
        broadcastUrl
        homepageUrl
        venueAddress
        venueLocationLatitude
        venueLocationLongitude
        eventCategoryIds
        eventCategories @client {
          ${AppShowEventCategories.fragments.eventCategoryFields}
        }
        profQualificationIds
        profQualifications @client {
          ${AppShowProfQualificationsList.fragments.profQualificationFields}
        }
        disciplineId
        discipline @client {
          id
          name
        }
        countryId
        country @client {
          ${AppShowCountry.fragments.country}
        }
        organiser {
          id
          name
          fullName
          logoPath
        }
        hostOrganisation {
          id
          name
        }
        ${AppShowCompetitionLevelNames.fragments.competitionLevels}
        difficultyGroups {
          id
          name
          nameShort
        }
        regionId
        region @client {
          id
          name
        }
        place
        ${AppShowEventCupComponent.fragments.cups}
        competitions {
          id
          eventId
          code
          startDate
          eventcodeId
          eventcode @client {
            ${AppShowEventcodes.fragments.eventcode}
          }
          difficultyLevelId
          difficultyLevel @client {
            ${AppShowDifficultyLevels.fragments.difficultyLevelFields}
          }
          competitionLevelId
          competitionLevel @client {
            ${AppShowCompetitionLevelNames.fragments.competitionLevel}
          }
          ${AppShowEventCupComponent.fragments.cupSeason}
          ${AppShowRegistrationDeadline.fragments.competitionFields}
        }
      }
    `
  }
};
</script>
<style scoped>
.textred {
  color: #771e24;
}
.current-status {
  font-weight: bold;
  font-size: 1.2em;
}

@media (max-width: 959px) {
  .remove-padding {
    padding: 0;
  }
}
</style>
